/**
 * timing.js — 应期日期候选生成层
 *
 * 先把 timing-rules.js 命中的规则翻译为具体日期并按同一公历日合并；
 * scoreTimingCandidates() 再给每个候选附上“规则支持强度”。
 *
 * 这里的 confidence 是规则可靠度，不是“事情会在这天发生”的概率。
 * 本模块始终不选主候选，也不按吉凶删减相互矛盾的日期。
 */

import { BRANCHES } from './ganzhi.js';
import { changSheng, SIX_COMBINE } from './analyze.js';
import {
  escapeBreakDates,
  escapeVoidDates,
  mergeCandidates,
  nextBranchDay,
  nextMonthBranches,
  nextYearBranch,
  timeScale,
  tombOpenDates
} from './date-candidates.js';
import {
  applicableTimingRules, completeTripleCombineEvidence,
  partialTripleCombineEvidence, TIMING_RULES
} from './timing-rules.js';
import { TIMING_CONFIDENCE } from './verdict-thresholds.js';

const RULE_BY_ID = new Map(TIMING_RULES.map((entry) => [entry.id, entry]));
const SCALE_RANK = { day: 0, month: 1, year: 2 };
const STRENGTH_LABEL = {
  direct: '原文直接规定',
  derived: '由原文卦例推演',
  inferred: '由通则推演'
};
const TIMING_NOTE = '按野鹤派规则推演的时点，非预测。各家规则不一，同卦可断出不同日期。';

const isStrong = (line) => line.state === '旺' || line.state === '相';
const isUnbroken = (line) => !line.isVoid && !line.isMonthBreak && !line.isDayBreak;
const clashBranch = (branch) => BRANCHES[(BRANCHES.indexOf(branch) + 6) % 12];

/**
 * 在卦上有用神时使用主爻；不上卦时只使用伏神，绝不误取其上的飞神。
 */
function timingUseLines(result) {
  if (result?.useGod?.主爻?.length) return result.useGod.主爻;
  return result?.useGod?.伏神 ? [result.useGod.伏神] : [];
}

function lineTarget(line, result, kind = '用神') {
  const target = {
    kind,
    position: line.position,
    relative: line.relative,
    ganzhi: line.ganzhi,
    branch: line.branch,
    element: line.element,
    state: line.state,
    isHidden: Boolean(line.isAbsent || (result?.useGod?.伏藏 && result.useGod.伏神 === line))
  };
  if (line.combineState) target.combineState = line.combineState;
  if (line.combinedBy?.length) target.combinedBy = line.combinedBy.map((actor) => ({ ...actor }));
  return target;
}

const attachLine = (candidate, line, result, kind = '用神') => ({
  ...candidate,
  target: lineTarget(line, result, kind),
  triggerBranch: candidate.branch
});

function linesForRule(ruleId, result) {
  const lines = timingUseLines(result);
  switch (ruleId) {
    case 'strong-value-day':
    case 'strong-value-month':
      return lines.filter((line) => isStrong(line) && isUnbroken(line));
    case 'strong-static-clash':
    case 'strong-static-clash-month':
    case 'strong-static-clash-year':
      return lines.filter((line) => isStrong(line) && !line.moving);
    case 'strong-moving-combine':
      return lines.filter((line) => isStrong(line) && line.moving);
    case 'void-escape-value':
    case 'void-escape-clash':
      return lines.filter((line) => line.isVoid);
    case 'month-break-fill':
    case 'month-break-exit':
    case 'month-break-combine':
      return lines.filter((line) => line.isMonthBreak);
    case 'tomb-open-clash':
      return lines.filter((line) => line.isTomb);
    case 'grave-revive':
      return lines.filter((line) => line.isGrave);
    case 'harmful-line-clash-day':
    case 'harmful-line-clash-month':
    case 'harmful-line-clash-year':
    case 'harmful-moving-line-combine':
      return result?.useGod?.overcomeBy || [];
    case 'combined-line-release':
      return (result?.yao || []).filter((line) => line.combineState === '合绊');
    default:
      return lines;
  }
}

function movingTransforms(result, kind) {
  return (result?.yao || []).filter((line) => line.moving && line.transform?.kind === kind);
}

function partialTripleMissingBranches(result) {
  return [...new Set(
    partialTripleCombineEvidence(result)
      .filter(({ qualifies }) => qualifies)
      .flatMap(({ missing }) => missing)
  )];
}

function candidateContext(result) {
  return result?.pillars || result;
}

function candidatesForRule(entry, result) {
  const context = candidateContext(result);
  const lines = linesForRule(entry.id, result);

  switch (entry.id) {
    case 'strong-value-day':
      return lines.map((line) => attachLine(nextBranchDay(line.branch, context), line, result));

    case 'strong-value-month':
      return lines.flatMap((line) => nextMonthBranches(line.branch, context)
        .map((candidate) => attachLine(candidate, line, result)));

    case 'strong-static-clash':
      return lines.map((line) => attachLine(nextBranchDay(clashBranch(line.branch), context), line, result));

    case 'strong-static-clash-month':
      return lines.flatMap((line) => nextMonthBranches(clashBranch(line.branch), context)
        .map((candidate) => attachLine(candidate, line, result)));

    case 'strong-static-clash-year':
      return lines.map((line) => attachLine(
        nextYearBranch(context, clashBranch(line.branch)), line, result
      ));

    case 'strong-moving-combine':
      return lines.map((line) => attachLine(nextBranchDay(SIX_COMBINE[line.branch], context), line, result));

    case 'weak-revive':
    case 'grave-revive':
      return lines.flatMap((line) => (result?.useGod?.reviveBranches || [])
        .map((branch) => attachLine(nextBranchDay(branch, context), line, result)));

    case 'void-escape-value':
      return lines.flatMap((line) => escapeVoidDates(line.branch, context)
        .filter(({ rule }) => rule === '值日')
        .map((candidate) => attachLine(candidate, line, result)));

    case 'void-escape-clash':
      return lines.flatMap((line) => escapeVoidDates(line.branch, context)
        .filter(({ rule }) => rule === '冲空')
        .map((candidate) => attachLine(candidate, line, result)));

    case 'month-break-fill':
      return lines.flatMap((line) => escapeBreakDates(line.branch, context)
        .filter(({ rule }) => rule === '实破')
        .map((candidate) => attachLine(candidate, line, result)));

    case 'month-break-exit':
      return lines.flatMap((line) => escapeBreakDates(line.branch, context)
        .filter(({ rule }) => rule === '出破月')
        .map((candidate) => attachLine(candidate, line, result)));

    case 'month-break-combine':
      return lines.flatMap((line) => escapeBreakDates(line.branch, context)
        .filter(({ rule }) => rule === '合破')
        .map((candidate) => attachLine(candidate, line, result)));

    case 'tomb-open-clash':
      return lines.flatMap((line) => tombOpenDates(line.element, context)
        .map((candidate) => attachLine(candidate, line, result)));

    case 'partial-triple-combine-fill':
      return partialTripleCombineEvidence(result)
        .filter(({ qualifies }) => qualifies)
        .flatMap((evidence) => evidence.missing.map((branch) => ({
          ...nextBranchDay(branch, context),
          target: {
            kind: '三合局',
            element: evidence.element,
            existing: evidence.existing.map((item) => ({ ...item })),
            missing: branch
          },
          triggerBranch: branch
        })));

    case 'triple-combine-open':
      return completeTripleCombineEvidence(result)
        .filter(({ qualifies }) => qualifies)
        .flatMap((evidence) => evidence.branches.map((member) => {
          const branch = clashBranch(member);
          return {
            ...nextBranchDay(branch, context),
            target: {
              kind: '三合局冲开',
              element: evidence.element,
              members: evidence.existing.map((item) => ({ ...item })),
              clashedMember: member
            },
            triggerBranch: branch
          };
        }));

    case 'longsheng-value-day':
      return lines.flatMap((line) => {
        const branch = BRANCHES.find((candidate) => changSheng(line.element, candidate) === '长生');
        return branch ? [attachLine(nextBranchDay(branch, context), line, result)] : [];
      });

    case 'harmful-line-clash-day':
      return lines.map((line) => attachLine(
        nextBranchDay(clashBranch(line.branch), context), line, result, '克神'
      ));

    case 'harmful-line-clash-month':
      return lines.flatMap((line) => nextMonthBranches(clashBranch(line.branch), context)
        .map((candidate) => attachLine(candidate, line, result, '克神')));

    case 'harmful-line-clash-year':
      return lines.map((line) => attachLine(
        nextYearBranch(context, clashBranch(line.branch)), line, result, '克神'
      ));

    case 'harmful-moving-line-combine':
      return lines.filter((line) => line.moving).map((line) => attachLine(
        nextBranchDay(SIX_COMBINE[line.branch], context), line, result, '克神'
      ));

    case 'combined-line-release':
      return lines.map((line) => attachLine(
        nextBranchDay(clashBranch(line.branch), context), line, result, '合绊爻'
      ));

    case 'transform-void-escape':
      return (result?.yao || [])
        .filter((line) => line.moving && line.transform?.toVoid)
        .flatMap((line) => escapeVoidDates(line.transform.toBranch, context)
          .map((candidate) => attachLine(candidate, line, result, '变爻')));

    case 'transform-advance-value-combine':
      return movingTransforms(result, '化进神').flatMap((line) => [
        attachLine(nextBranchDay(line.branch, context), line, result, '动爻'),
        attachLine(nextBranchDay(SIX_COMBINE[line.branch], context), line, result, '动爻')
      ]);

    case 'transform-retreat-uncertain':
      return movingTransforms(result, '化退神').flatMap((line) => {
        const branch = line.transform.toBranch;
        return [
          attachLine(nextBranchDay(branch, context), line, result, '动爻'),
          attachLine(nextBranchDay(clashBranch(branch), context), line, result, '动爻')
        ];
      });

    // 这两条只修饰后续裁决；现有 patternFlags 没有提供一个可直接换算的日期支。
    case 'six-clash-fast':
    case 'six-combine-open':
      return [];

    default:
      return [];
  }
}

function unresolvedReason(entry, result) {
  switch (entry.id) {
    case 'six-clash-fast':
      return '六冲只说明应期不宜久远；最近候选属于下一阶段裁决，本层不生成日期';
    case 'six-combine-open':
      return '现有 patternFlags 只标识六合卦，未结构化指出被合住的具体爻支，无法计算冲开日';
    case 'partial-triple-combine-fill':
      return partialTripleMissingBranches(result).length
        ? '三合缺支未能换算为日期'
        : '未找到仅缺一支的三合局；局已成或不足两支时没有可补的缺字';
    default:
      return '规则已命中，但现有结构化字段不足以换算日期';
  }
}

function parseDate(date) {
  const [year, month, day] = date.split('-').map(Number);
  return { year, month, day };
}

/**
 * 同日命中不同时间尺度时，保留较宽的规则尺度，避免把强制 month 的规则
 * 降成 day；若规则没有合法 scale，才回退到 timeScale(daysAway)。
 */
function mergedScale(merged, rawCandidates) {
  const scales = rawCandidates
    .filter((candidate) => candidate.date === merged.date)
    .map((candidate) => RULE_BY_ID.get(candidate.rule)?.scale)
    .filter((scale) => Object.hasOwn(SCALE_RANK, scale));
  if (!scales.length) return timeScale(merged.daysAway);
  return scales.reduce((widest, scale) => (
    SCALE_RANK[scale] > SCALE_RANK[widest] ? scale : widest
  ));
}

/**
 * 把命中的规则转成具体日期候选。
 *
 * candidates 只按 daysAway 升序组织；此顺序不代表置信度或优先级。
 */
export function generateTimingCandidates(result) {
  const matched = applicableTimingRules(result);
  const rawCandidates = [];
  const unresolved = [];

  for (const entry of matched) {
    try {
      const generated = candidatesForRule(entry, result);
      if (!generated.length) {
        unresolved.push({ ruleId: entry.id, reason: unresolvedReason(entry, result) });
        continue;
      }
      for (const candidate of generated) {
        rawCandidates.push({
          ...candidate,
          dateRule: candidate.rule || null,
          // mergeCandidates() 用 rule/source 合并；输出阶段再恢复完整规则对象。
          rule: entry.id,
          source: entry.source
        });
      }
    } catch (error) {
      unresolved.push({
        ruleId: entry.id,
        reason: error instanceof Error ? error.message : String(error)
      });
    }
  }

  const candidates = mergeCandidates([rawCandidates]).map((merged) => ({
    date: parseDate(merged.date),
    ganzhi: merged.ganzhi,
    daysAway: merged.daysAway,
    scale: mergedScale(merged, rawCandidates),
    rules: merged.rules.map(({ rule: id }) => {
      const entry = RULE_BY_ID.get(id);
      const evidence = rawCandidates.filter((raw) => raw.date === merged.date && raw.rule === id);
      const unique = (items) => [...new Map(items.map((item) => [JSON.stringify(item), item])).values()];
      return {
        id: entry.id,
        name: entry.name,
        source: entry.source,
        targets: unique(evidence.map(({ target }) => target).filter(Boolean)),
        triggerBranches: [...new Set(evidence.map(({ triggerBranch }) => triggerBranch).filter(Boolean))],
        dateRules: [...new Set(evidence.map(({ dateRule }) => dateRule).filter(Boolean))]
      };
    })
  }));

  return { candidates, unresolved };
}

const timingLineRef = (target) => `${target.position}爻${target.relative}${target.ganzhi}`;
const timingDateText = (candidate) => {
  const { year, month, day } = candidate.date;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${candidate.ganzhi}`;
};

function timingActionText(ruleId, candidate, rule) {
  const date = timingDateText(candidate);
  const branch = rule.triggerBranches.join('、');
  switch (ruleId) {
    case 'strong-value-day': return `${date}日值日`;
    case 'strong-value-month': return `${date}起进入${branch}月值月`;
    case 'strong-static-clash': return `${date}日冲静爻`;
    case 'strong-static-clash-month': return `${date}起进入${branch}月冲静爻`;
    case 'strong-static-clash-year': return `${date}起进入${branch}年冲静爻`;
    case 'strong-moving-combine': return `${date}日合动爻`;
    case 'weak-revive': return `${date}日以${branch}支生扶`;
    case 'void-escape-value': return `${date}日值日出空`;
    case 'void-escape-clash': return `${date}日冲空`;
    case 'month-break-fill': return `${date}日值本支实破`;
    case 'month-break-exit': return `${date}起交节出破月`;
    case 'month-break-combine': return `${date}日合破`;
    case 'tomb-open-clash': return `${date}日冲墓`;
    case 'grave-revive': return `${date}日以${branch}支生扶`;
    case 'partial-triple-combine-fill': return `${date}日补齐${branch}支`;
    case 'triple-combine-open': return `${date}日以${branch}支冲开三合局`;
    case 'longsheng-value-day': return `${date}日逢${branch}支长生`;
    case 'harmful-line-clash-day': return `${date}日以${branch}支冲去克神`;
    case 'harmful-line-clash-month': return `${date}起进入${branch}月冲去克神`;
    case 'harmful-line-clash-year': return `${date}起进入${branch}年冲去克神`;
    case 'harmful-moving-line-combine': return `${date}日合该动爻，动而逢合为合绊`;
    case 'combined-line-release': return `${date}日冲开合绊`;
    case 'transform-void-escape': return rule.dateRules.includes('值日')
      ? `${date}日变爻值日出空`
      : `${date}日冲变爻之空`;
    case 'transform-advance-value-combine': return `${date}日逢值或逢合`;
    case 'transform-retreat-uncertain': return `${date}日逢值或逢冲`;
    default: return `${date}日对应${rule.name}`;
  }
}

function timingStateText(ruleId, target) {
  const subject = target.isHidden ? '伏神用神' : target.kind === '变爻' ? '变爻所出之支' : '用神';
  if (ruleId.startsWith('void-escape-')) return `${subject}${timingLineRef(target)}旬空`;
  if (ruleId.startsWith('month-break-')) return `${subject}${timingLineRef(target)}月破`;
  if (ruleId === 'tomb-open-clash') return `${subject}${timingLineRef(target)}入墓`;
  if (ruleId === 'grave-revive') return `${subject}${timingLineRef(target)}入绝`;
  if (ruleId === 'weak-revive') return `${subject}${timingLineRef(target)}${target.state || ''}而待生扶`;
  if (ruleId === 'strong-static-clash') return `${subject}${timingLineRef(target)}旺相而静`;
  if (ruleId === 'strong-static-clash-month' || ruleId === 'strong-static-clash-year') {
    return `${subject}${timingLineRef(target)}旺相而静`;
  }
  if (ruleId === 'strong-moving-combine') return `${subject}${timingLineRef(target)}旺相而动`;
  if (ruleId === 'strong-value-day' || ruleId === 'strong-value-month') return `${subject}${timingLineRef(target)}旺相不空不破`;
  if (ruleId === 'transform-void-escape') return `动爻${timingLineRef(target)}化空`;
  if (ruleId === 'transform-advance-value-combine') return `动爻${timingLineRef(target)}化进神`;
  if (ruleId === 'transform-retreat-uncertain') return `动爻${timingLineRef(target)}化退神`;
  if (ruleId === 'longsheng-value-day') return `${subject}${timingLineRef(target)}待长生支值日`;
  if (ruleId.startsWith('harmful-line-clash-')) return `克用神之${timingLineRef(target)}`;
  if (ruleId === 'harmful-moving-line-combine') return `克用神之${timingLineRef(target)}发动`;
  if (ruleId === 'combined-line-release') return `${timingLineRef(target)}发动，当前动而逢合为合绊`;
  return `${subject}${timingLineRef(target)}`;
}

/**
 * 把候选层已经算出的“状态—目标爻—日期”证据组织成 keyPoints。
 * 只说明哪条状态在何日/何月满足古籍规则，不写届时吉凶或是否成事。
 */
export function timingKeyPoints(result) {
  const generated = generateTimingCandidates(result);
  const grouped = new Map();
  for (const candidate of generated.candidates) {
    for (const rule of candidate.rules) {
      for (const target of rule.targets || []) {
        if (target.kind === '三合局冲开') {
          const refs = target.members.map(({ position }) => timingLineRef(lineTarget(result.yao[position - 1], result)));
          const key = `triple-open:${target.element}`;
          const item = grouped.get(key) || {
            subject: `${refs.join('、')}构成三合${target.element}局`,
            actions: []
          };
          item.actions.push(timingActionText(rule.id, candidate, rule));
          grouped.set(key, item);
          continue;
        }
        if (target.kind === '三合局') {
          const refs = target.existing.map(({ position }) => timingLineRef(lineTarget(result.yao[position - 1], result)));
          const key = `triple:${target.element}:${target.missing}`;
          const item = grouped.get(key) || {
            subject: `${refs.join('、')}构成三合${target.element}局已有两支，缺${target.missing}`,
            actions: []
          };
          item.actions.push(timingActionText(rule.id, candidate, rule));
          grouped.set(key, item);
          continue;
        }
        const key = `${target.kind}:${target.position}:${target.ganzhi}:${rule.id}`;
        const item = grouped.get(key) || {
          subject: timingStateText(rule.id, target),
          actions: []
        };
        item.actions.push(timingActionText(rule.id, candidate, rule));
        grouped.set(key, item);
      }
    }
  }
  return [...grouped.values()].map(({ subject, actions }) => (
    `${subject}，候选时点为${[...new Set(actions)].join('；或')}。`
  ));
}

function useGodStateTags(result) {
  const lines = timingUseLines(result);
  const tags = [];
  const add = (condition, label) => {
    if (condition && !tags.includes(label)) tags.push(label);
  };
  add(lines.some((line) => ['休', '囚', '死'].includes(line.state)), '休囚');
  add(lines.some((line) => line.isVoid), '旬空');
  add(lines.some((line) => line.isMonthBreak), '月破');
  add(lines.some((line) => line.isDayBreak), '日破');
  add(lines.some((line) => line.isTomb), '入墓');
  add(lines.some((line) => line.isGrave), '入绝');
  add(result?.useGod?.isAbsent, '伏藏');
  return tags;
}

const RULE_TARGET_STATE = Object.freeze({
  'weak-revive': '休囚',
  'void-escape-value': '旬空',
  'void-escape-clash': '旬空',
  'month-break-fill': '月破',
  'month-break-exit': '月破',
  'month-break-combine': '月破',
  'tomb-open-clash': '入墓',
  'grave-revive': '入绝'
});

// 休囚是旺衰五态，不是需要另行“解除”的特殊状态。它作为规则目标时，
// 会受并存的空、破、墓、绝、伏藏干扰；反过来，冲墓等专门规则不因用神
// 同时休囚而降低清晰度，因为该候选已经直接处理了墓这一特殊状态。
const SPECIAL_STATE_TAGS = new Set(['旬空', '月破', '日破', '入墓', '入绝', '伏藏']);

function clarityEvidence(result, candidate) {
  const tags = useGodStateTags(result);
  const targets = [...new Set(candidate.rules
    .map(({ id }) => RULE_TARGET_STATE[id])
    .filter(Boolean))];
  const remaining = tags.filter((tag) => SPECIAL_STATE_TAGS.has(tag) && !targets.includes(tag));
  const k = remaining.length;
  const { clarityFactor } = TIMING_CONFIDENCE;
  const value = k === 0
    ? clarityFactor.single
    : k === 1
      ? clarityFactor.two
      : clarityFactor.threePlus;
  const targetText = targets.length ? `${targets.join('、')}规则` : '该候选规则';
  const detail = k === 0
    ? `${targetText}无其他特殊状态干扰`
    : `${targetText}另有${remaining.join('、')}干扰`;
  return { value, detail };
}

function strongestRuleEvidence(candidate) {
  const strengths = candidate.rules.map(({ id }) => {
    const entry = RULE_BY_ID.get(id);
    return {
      entry,
      value: TIMING_CONFIDENCE.ruleStrength[entry.strength]
    };
  });
  const max = Math.max(...strengths.map(({ value }) => value));
  const strongest = strengths.filter(({ value }) => value === max);
  const details = strongest.map(({ entry }) => (
    `${entry.name}（${entry.source.book}·${entry.source.chapter}）`
  ));
  const grade = strongest[0].entry.strength;
  return {
    value: max,
    detail: `${details.join('、')}，${STRENGTH_LABEL[grade]}`
  };
}

function repeatEvidence(candidate) {
  const count = candidate.rules.length;
  const { increment, max } = TIMING_CONFIDENCE.repeatBonus;
  return {
    value: Math.min(1 + increment * (count - 1), max),
    detail: `${count} 条规则指向同日`
  };
}

const clamp = (value) => Math.min(1, Math.max(0, value));
const roundConfidence = (value) => Math.round(value * 10000) / 10000;

/**
 * 为全部日期候选附上可还原的规则置信度；不会挑选或新增“首选日期”。
 */
export function scoreTimingCandidates(result) {
  const generated = generateTimingCandidates(result);
  const candidates = generated.candidates.map((candidate) => {
    const clarity = clarityEvidence(result, candidate);
    const ruleStrength = strongestRuleEvidence(candidate);
    const repeatBonus = repeatEvidence(candidate);
    const confidence = roundConfidence(clamp(
      ruleStrength.value * repeatBonus.value * clarity.value
    ));
    return {
      ...candidate,
      confidence,
      factors: [
        { name: '规则强度', ...ruleStrength },
        { name: '重复指向', ...repeatBonus },
        { name: '状态清晰度', ...clarity }
      ]
    };
  });
  return {
    candidates,
    unresolved: generated.unresolved,
    note: TIMING_NOTE
  };
}
